import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { fetchNews, createNews, updateNews, deleteNews } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Image as ImageIcon, Calendar, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { SingleImageUploader } from '@/components/SingleImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManageNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title_fr: '',
    title_en: '',
    content_fr: '',
    content_en: '',
    category: 'news',
    image_url: '',
    published: true
  });

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const data = await fetchNews();
      setNews(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des actualités');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingNews(null);
    setFormData({
      title_fr: '',
      title_en: '',
      content_fr: '',
      content_en: '',
      category: 'news',
      image_url: '',
      published: true
    });
    setDialogOpen(true);
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title_fr: newsItem.title_fr,
      title_en: newsItem.title_en,
      content_fr: newsItem.content_fr,
      content_en: newsItem.content_en,
      category: newsItem.category,
      image_url: newsItem.image_url || '',
      published: newsItem.published
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingNews) {
        await updateNews(editingNews.news_id, formData);
        toast.success('Actualité mise à jour avec succès');
      } else {
        await createNews(formData);
        toast.success('Actualité créée avec succès');
      }
      setDialogOpen(false);
      loadNews();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNews(id);
      toast.success('Actualité supprimée avec succès');
      loadNews();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleImageChange = (imageUrl) => {
    setFormData({ ...formData, image_url: imageUrl });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      news: 'Actualité',
      event: 'Événement',
      announcement: 'Annonce'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      news: 'bg-blue-100 text-blue-700',
      event: 'bg-purple-100 text-purple-700',
      announcement: 'bg-amber-100 text-amber-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  return (
    <AdminLayout>
      <div data-testid="manage-news" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Gérer les actualités</h1>
            <p className="text-slate-600 mt-1">Créez et gérez les actualités et événements</p>
          </div>
          <Button 
            data-testid="add-news-btn" 
            onClick={handleAdd} 
            className="bg-[#127abb] hover:bg-[#0e6292] rounded-full"
          >
            <Plus size={20} className="mr-2" />
            Ajouter
          </Button>
        </div>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingNews ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
              </DialogTitle>
              <DialogDescription>
                {editingNews 
                  ? 'Modifiez les informations de l\'actualité' 
                  : 'Créez une nouvelle actualité ou événement'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload */}
              <SingleImageUploader
                imageUrl={formData.image_url}
                onChange={handleImageChange}
                category="news"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Titre (FR) *</label>
                  <Input 
                    value={formData.title_fr} 
                    onChange={(e) => setFormData({...formData, title_fr: e.target.value})} 
                    required 
                    placeholder="Titre en français"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Titre (EN) *</label>
                  <Input 
                    value={formData.title_en} 
                    onChange={(e) => setFormData({...formData, title_en: e.target.value})} 
                    required 
                    placeholder="Title in English"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Contenu (FR) *</label>
                <Textarea 
                  value={formData.content_fr} 
                  onChange={(e) => setFormData({...formData, content_fr: e.target.value})} 
                  required 
                  rows={4} 
                  placeholder="Contenu de l'actualité en français..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Contenu (EN) *</label>
                <Textarea 
                  value={formData.content_en} 
                  onChange={(e) => setFormData({...formData, content_en: e.target.value})} 
                  required 
                  rows={4} 
                  placeholder="News content in English..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Catégorie</label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({...formData, category: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="news">Actualité</SelectItem>
                      <SelectItem value="event">Événement</SelectItem>
                      <SelectItem value="announcement">Annonce</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Statut</label>
                  <Select 
                    value={formData.published.toString()} 
                    onValueChange={(value) => setFormData({...formData, published: value === 'true'})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Publié</SelectItem>
                      <SelectItem value="false">Brouillon</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#127abb] hover:bg-[#0e6292]">
                  {editingNews ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-[#127abb] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : news.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="text-slate-400" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune actualité</h3>
            <p className="text-slate-600 mb-4">Commencez par créer votre première actualité.</p>
            <Button onClick={handleAdd} className="bg-[#127abb] hover:bg-[#0e6292]">
              <Plus size={20} className="mr-2" />
              Créer une actualité
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item) => (
              <div 
                key={item.news_id} 
                data-testid={`news-card-${item.news_id}`}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Image */}
                <div className="aspect-video bg-slate-100 relative">
                  {item.image_url ? (
                    <img
                      src={item.image_url.startsWith('/') ? `${BACKEND_URL}${item.image_url}` : item.image_url}
                      alt={item.title_fr}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`absolute inset-0 flex items-center justify-center bg-slate-100 ${item.image_url ? 'hidden' : ''}`}
                  >
                    <ImageIcon className="text-slate-400" size={48} />
                  </div>
                  
                  {/* Status badge */}
                  <div className="absolute top-3 right-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.published 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {item.published ? 'Publié' : 'Brouillon'}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(item.category)}`}>
                      <Tag size={12} className="inline mr-1" />
                      {getCategoryLabel(item.category)}
                    </span>
                  </div>
                  
                  <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">
                    {item.title_fr}
                  </h3>
                  
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                    {item.content_fr.substring(0, 100)}...
                  </p>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Calendar size={12} />
                      {format(new Date(item.created_at), 'dd MMM yyyy', { locale: fr })}
                    </span>
                    
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleEdit(item)}
                        data-testid={`edit-news-${item.news_id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`delete-news-${item.news_id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer cette actualité ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. L'actualité "{item.title_fr}" sera définitivement supprimée.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(item.news_id)} 
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManageNews;
